import { isDefined, isNotDefined } from '@sgme/fp';
import { HStack, Spacer, VStack } from '@sgme/ui';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import ActionButtonsForEditOrders from '@/App/tiles/orders/components/ActionButtonsForEditOrders.tsx';
import { ExecutedTileField } from '@/App/tiles/orders/components/ExecutedTileField.tsx';
import { ProgressBar } from '@/App/tiles/orders/components/ProgressBar.tsx';
import { Summary } from '@/App/tiles/orders/components/Summary.tsx';
import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus.tsx';
import {
  useFixingIsChecking,
  useFixingIsModifyingField,
  useFixingTileId,
} from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { FixingEditingCommercialMargin } from '@/App/tiles/orders/FixingOrder/FixingOrderEditingTile/FixingEditingCommercialMargin.tsx';
import { FixingEditingMarginType } from '@/App/tiles/orders/FixingOrder/FixingOrderEditingTile/FixingEditingMarginType.tsx';
import { FixingEditingNotional } from '@/App/tiles/orders/FixingOrder/FixingOrderEditingTile/FixingEditingNotional.tsx';
import { FixingEditingPriceType } from '@/App/tiles/orders/FixingOrder/FixingOrderEditingTile/FixingEditingPriceType.tsx';
import { useAccount } from '@/App/tiles/orders/hooks.tsx';
import { getStatusClass, mapStatusToColor } from '@/App/tiles/orders/utils.ts';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { UsertypeGuard } from '@/components/UsertypeGuard.tsx';
import { getConfig } from '@/config/config.ts';
import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { finalStatuses, type FixingPriceType } from '@/store/bulkOrder/state.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { useUpdateFixingMutation } from '@/store/orders/fixing/api/index.ts';
import {
  selectFixingTile,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromFixingTile,
} from '@/store/orders/fixing/selectors.ts';
import type { FixingMarginType } from '@/store/orders/fixing/state.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import type { ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { removeTile } from '@/store/tiles/slice.ts';

const FixingOrderEditingTile = () => {
  const track = useTrackEvent();

  const tileId = useFixingTileId();
  const isChecking = useFixingIsChecking();
  const isModifyingField = useFixingIsModifyingField();

  const dispatch = useAppDispatch();
  const userContextValue = useUserContext();

  const [updateFixing] = useUpdateFixingMutation({
    selectFromResult: () => ({}),
  });

  const [notional, setNotional] = useState<number | undefined>(undefined);
  const [fixingMarginType, setFixingMarginType] = useState<FixingMarginType | undefined>(undefined);
  const [fixingPriceType, setFixingPriceType] = useState<FixingPriceType | undefined>(undefined);
  const [commercialMargin, setCommercialMargin] = useState<number | undefined>(undefined);

  const tradeId = useAppSelector((state) => selectTradeIdFromFixingTile(state, tileId));
  const tile = useAppSelector((state) => selectFixingTile(state, tileId));

  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const account = useAccount(orderFromBlotter);

  const isFinal = finalStatuses.includes(orderFromBlotter?.status ?? '');
  const color = mapStatusToColor(orderFromBlotter?.status);

  const canTrade = checkIfUserCanTrade(userContextValue);

  const isDisabled = isNotDefined(tile) || !tile.properties.isReadyToSubmit || !canTrade || isModifyingField;

  function handleClose() {
    track('Order action', 'Remove Order');
    dispatch(removeTile(tileId));
  }

  function cancelChange() {
    track('Order action', 'Cancel Editing Order');
    dispatch(ordersSlice.actions.changeOrderMode({ mode: 'VIEW', tileId }));
  }

  function amendChanges() {
    track('Order action', 'Amend Order');
    if (isNotDefined(orderFromBlotter)) {
      console.error(`[handleSubmit] Tile with id ${tileId} not found in state.`);
      return;
    }

    const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
      ? orderFromBlotter.currencyPair.split('/')
      : ['', ''];

    if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(tradeId)) {
      console.error(`[handleValidateFixing] Invalid currency pair: ${currency1}/${currency2}`);
      return;
    }

    const marginType = isDefined(fixingMarginType)
      ? fixingMarginType
      : (orderFromBlotter.fixingMarginType as FixingMarginType);
    const priceType = isDefined(fixingPriceType)
      ? fixingPriceType
      : (orderFromBlotter.fixingPriceType as FixingPriceType);

    updateFixing({
      orderRef: tradeId,
      tileId,
      currency1: currency1,
      currency2: currency2,
      way: orderFromBlotter?.yourWay === 'Buy' ? 'buy' : 'sell',
      notional: isDefined(notional) ? notional : Number(orderFromBlotter.notional ?? 0),
      notionalCurrencyIndex: orderFromBlotter.notionalCurrency === currency1 ? 1 : 2,
      commercialMargin:
        marginType === 'MarginInBps'
          ? isDefined(commercialMargin)
            ? commercialMargin
            : Number(orderFromBlotter.marginInBps ?? 0)
          : undefined,
      fixingReference: orderFromBlotter.fixingBenchmark ?? '',
      fixingDateUtc: orderFromBlotter.fixingDateUtc ?? undefined,
      ...(tile?.properties.isStandardGroup
        ? { fixingTime: tile.properties.fixingTime }
        : { fixingPlace: tile?.properties.fixingPlace }),
      fixingPriceType: tile?.properties.isCcyForcedBidAsk ? 'BIDASK' : priceType,
      fixingMarginType: marginType,
    })
      .unwrap()
      .then(() => {
        track('Order action', 'Amend Order successful');
        dispatch(ordersSlice.actions.changeOrderMode({ mode: 'VIEW', tileId }));
      })
      .catch(({ errors }: { type: 'failure'; errors: ValidationResponseDetailContent[] }) => {
        console.error('Failed to submit take profit order:', errors);
        if (errors.some((error) => error.errorDescription === 'timeout')) {
          const params: SendHelpRequestRequest = {
            config: 'orderpending',
            details: JSON.stringify({
              payload: errors,
              orderIdToUpdate: tileId,
            }),
            data: {
              orderDescription: 'creation',
              orderType: 'limit',
              /**
               * send 'g10' or 'emerging' on order creation when we are able to differentiate
               */
              orderCurrencyType: 'unknown',
              orderId: tileId,
            },
            templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
            templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
          };

          logger.logError('Order submission failed on timeout for {tileId_s}', tileId);
          dispatch(sendHelpRequestThunk(params));
        }
      });
  }

  const shouldDisplayCommercialMargin =
    (orderFromBlotter?.fixingMarginType === 'MarginInBps' && fixingMarginType === undefined) ||
    fixingMarginType === 'MarginInBps';

  if (isNotDefined(orderFromBlotter)) {
    console.error(`[FixingOrderExecutedTile] Tile with id ${tileId} not found in blotter.`);
    return <ExecutionPendingOrderStatus />;
  }

  return (
    <AutoAdvanceForm className="bg-lvl1 w-100 h-100">
      <VStack className="bg-lvl1" verticalAlignment="start" fullWidth fullHeight>
        <HStack className="m-2">
          <div>
            <span className="fw-bold">Order {tradeId} </span>
            <span className={getStatusClass(orderFromBlotter?.status)}>{orderFromBlotter?.status}</span>
          </div>
          <button type="button" className="btn btn-flat-primary" onClick={handleClose} data-e2e="close-tile">
            <em className="icon icon-sm">close</em>
          </button>
        </HStack>

        <div className={`bg-${color} bg-opacity-10 text-${color} p-3 my-3`}>
          <ProgressBar
            executedAmount={orderFromBlotter?.executedAmount}
            notional={orderFromBlotter?.notional}
            color={color}
            isFinal={isFinal}
          />

          <div className="my-3">
            <span className="summary-text">
              <Summary
                account={account}
                orderType={orderFromBlotter?.orderType ?? 'Call'}
                way={orderFromBlotter?.yourWay ?? null}
                isGtc={orderFromBlotter?.isGtc ?? false}
                notional={orderFromBlotter?.notional ?? null}
                currency={orderFromBlotter?.notionalCurrency ?? null}
                limitPrice={orderFromBlotter?.limitPrice ?? null}
                expiryDay={orderFromBlotter?.expiryDay ?? null}
                expiryTime={orderFromBlotter?.expiryTime ?? null}
                currencyPair={orderFromBlotter?.currencyPair ?? null}
                fixingPlaceOrTime={orderFromBlotter?.fixingTime ?? orderFromBlotter?.fixingPlace ?? null}
                fixingDate={orderFromBlotter?.fixingDateUtc ?? null}
                fixingRef={orderFromBlotter?.fixingBenchmark ?? null}
                priceType={(orderFromBlotter?.fixingPriceType as FixingPriceType) ?? null}
                finalStatus={isFinal}
              />
            </span>
          </div>
        </div>

        <div>
          <ExecutedTileField label="currencyPair" value={orderFromBlotter?.currencyPair} />
          <ExecutedTileField label="orderType" value={orderFromBlotter?.orderType} />
          <ExecutedTileField label="way" value={`${orderFromBlotter?.yourWay} ${orderFromBlotter?.notionalCurrency}`} />
          <ExecutedTileField label="fixingReference" value={orderFromBlotter?.fixingBenchmark} />
          <ExecutedTileField label="fixingDate" value={orderFromBlotter?.fixingDateUtc} />
        </div>

        <span className="text-muted m-8px">
          <FormattedMessage id="order.result.editableAttributes" />
        </span>
        <div
          className="mx-16px gap-8px"
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr)',
            gridTemplateRows: '20px',
            alignItems: 'center',
          }}
        >
          <FixingEditingNotional notional={notional} setNotional={setNotional} />
          <UsertypeGuard allow="Internal Sales">
            <FixingEditingMarginType
              priceType={fixingPriceType}
              marginType={fixingMarginType}
              setMarginType={setFixingMarginType}
            />
            <FixingEditingPriceType
              priceType={fixingPriceType}
              setMarginType={setFixingMarginType}
              setPriceType={setFixingPriceType}
            />
            {shouldDisplayCommercialMargin && (
              <FixingEditingCommercialMargin
                commercialMargin={commercialMargin}
                setCommercialMargin={setCommercialMargin}
              />
            )}
          </UsertypeGuard>
        </div>
        <Spacer />
        <ActionButtonsForEditOrders cancelChange={cancelChange} amendChanges={amendChanges} disabled={isDisabled} />
      </VStack>
      {isChecking && (
        <div className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75">
          <div className="spinner blinker-lg" />
        </div>
      )}
    </AutoAdvanceForm>
  );
};

export default FixingOrderEditingTile;
